/**
 * Amount and comprobante helpers shared by the invoice (emission) flow.
 *
 * Kept free of imports so it can be verified outside the browser.
 *
 * Reglas que protegen el flujo:
 *   - un `montoFacturacion` en 0 o "" no es un monto real, así que el diálogo de
 *     emisión cae a `importeTotal` en vez de mostrar S/ 0;
 *   - `numeroComprobante` se limpia SOLO cuando hay un comprobante inequívoco:
 *     si el texto tiene dos referencias ("... REEMPLAZA A ...", "... (ANULADO)")
 *     o dígitos sueltos ("E001-289/293"), no se toca, porque elegir una podría
 *     asociar el pago a la factura anulada o a otra venta;
 *   - una venta puede acumular VARIAS facturas separadas por coma
 *     ("F001-1, F001-2"): la coma es el único separador aceptado y la lista solo
 *     se reconoce cuando TODOS sus segmentos son SERIE-NUMERO. Un valor con dos
 *     referencias y sin coma sigue siendo ambiguo y no se interpreta como lista;
 *   - un valor sin SERIE-NUMERO ("000136", "SN") se conserva tal cual: nunca se
 *     borra un dato guardado;
 *   - solo se manda al sync de pagos el número que se puede partir en
 *     serie + número sin ambigüedad, y solo cuando la venta tiene UNA factura.
 */

// La serie es alfanumérica y de largo variable: F001, FC01, 001, 01, EB001,
// 002812. El guion puede venir con espacios alrededor ("001 - 000496").
const COMPROBANTE_PATTERN = /([A-Z0-9]{1,6})\s*-\s*(\d{1,8})/g

// Lo que puede quedar alrededor de la referencia para considerarla limpia:
// espacios, barras, puntos y una anotación entre paréntesis ("(ANULADO)").
// Cualquier otra cosa (" k $", ",", "ANULADA X") se conserva sin tocar.
const RESTO_PERMITIDO = /^[\s/.]*(\([^()]*\))?[\s/.]*$/

/**
 * Normalise whatever the API or a form field sends into a number >= 0.
 * Accepts numbers, "49", "49.00", "S/ 110.50"; anything invalid becomes 0.
 */
export const parseMonto = (value) => {
    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0 ? value : 0
    }
    if (typeof value !== 'string') return 0
    const parsed = parseFloat(value.replace(/[^0-9.-]+/g, ''))
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

/**
 * Amount the invoice is issued for: `montoFacturacion` only when it is a real
 * amount, otherwise the sale total.
 */
export const montoComprobante = (item) => {
    if (!item) return 0
    return parseMonto(item.montoFacturacion) || parseMonto(item.importeTotal)
}

/**
 * Value safe to persist: a zero or empty amount must be stored as null so it
 * keeps falling back to `importeTotal` instead of freezing the invoice at 0.
 */
export const montoParaGuardar = (value) => {
    const monto = parseMonto(value)
    return monto > 0 ? Number(monto.toFixed(2)) : null
}

/**
 * SERIE-NUMERO sin ambigüedad, o null.
 *
 * Requiere UNA sola referencia y que fuera de ella no queden dígitos: así
 * "F001-1424 (ANULADO)/" se resuelve a "F001-1424", mientras que
 * "F001-1732 REEMPLAZA A F001-1390", "B001-429/ B001-425 (ANULADO)" y
 * "E001-289/293" devuelven null y no se reescriben.
 */
const refUnica = (value) => {
    if (typeof value !== 'string') return null
    const text = value.trim().toUpperCase()
    if (!text) return null

    const matches = [...text.matchAll(COMPROBANTE_PATTERN)]
    if (matches.length !== 1) return null

    const match = matches[0]
    const numero = Number(match[2])
    if (!Number.isInteger(numero) || numero <= 0) return null

    // Lo que queda alrededor de la referencia no puede tener otro número ni
    // texto que no sea una anotación: eso sería un sufijo ("/293") o un
    // comentario pegado al número ("F001-6 k $").
    const resto = text.replace(match[0], '')
    if (/\d/.test(resto) || !RESTO_PERMITIDO.test(resto)) return null

    // Se conserva el texto original del número: "001-00489" mantiene sus ceros.
    return `${match[1]}-${match[2]}`
}

// La coma es el separador canónico de la lista de facturas de una venta.
const SEPARADOR_LISTA = ','

const textoComprobante = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null
    if (typeof value !== 'string') return null
    const texto = value.trim().toUpperCase()
    return texto === '' ? null : texto
}

/**
 * Comprobantes identificables de la venta, en orden y sin repetir.
 *
 * Devuelve [] cuando el valor no es una lista limpia: sin coma se resuelve como
 * siempre (una única referencia) y con coma se exige que TODOS los segmentos
 * sean SERIE-NUMERO. Un segmento sucio invalida la lista entera en vez de elegir
 * uno: asociar un pago a la factura equivocada es peor que no asociarlo.
 */
export const dividirComprobantes = (value) => {
    const texto = textoComprobante(value)
    if (!texto) return []

    const partes = texto
        .split(SEPARADOR_LISTA)
        .map((parte) => parte.trim())
        .filter((parte) => parte !== '')

    const refs = []
    for (const parte of partes) {
        const ref = refUnica(parte)
        if (!ref) return []
        if (!refs.includes(ref)) refs.push(ref)
    }
    return refs
}

/**
 * Agrega la factura recién emitida a lo que la venta ya tiene guardado.
 *
 * - lista limpia o vacía: se unen las referencias en formato canónico `, `
 *   sin duplicar y sin perder el orden;
 * - valor legacy/ambiguo ("000136", "SN", "... REEMPLAZA A ..."): se conserva
 *   el texto guardado tal cual y el número nuevo se agrega al final;
 * - sin número nuevo identificable no se toca nada.
 */
export const agregarComprobante = (actual, nuevo) => {
    const textoActual = textoComprobante(actual)
    const refsNuevas = dividirComprobantes(nuevo)
    if (refsNuevas.length === 0) return textoActual

    const refsActuales = dividirComprobantes(textoActual)
    if (!textoActual || refsActuales.length > 0) {
        refsNuevas.forEach((ref) => {
            if (!refsActuales.includes(ref)) refsActuales.push(ref)
        })
        return refsActuales.join(', ')
    }

    return `${textoActual}, ${refsNuevas.join(', ')}`
}

/**
 * Valor a guardar en la venta.
 *
 * Si hay un comprobante inequívoco, se guarda normalizado (sin espacios ni
 * anotaciones). Si no, se conserva el texto original: los comprobantes legacy
 * ("000136", "SN") y las referencias múltiples no se destruyen.
 */
export const normalizarComprobante = (value) => {
    // Un número (si facturación alguna vez serializa `number` como JSON number)
    // se guarda como texto en vez de destruirse.
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : null
    }
    if (typeof value !== 'string') return null
    const original = value.trim().toUpperCase()
    if (!original) return null

    // Lista de facturas: se guarda canónica (", "), sin repetir y en orden.
    const refs = dividirComprobantes(original)
    if (refs.length > 1) return refs.join(', ')
    if (refs.length === 1) return refs[0]

    return original
}

/**
 * True solo cuando el texto se puede partir en serie + número sin ambigüedad y
 * la venta tiene UNA sola factura, que es lo que necesita el sync de pagos para
 * encontrar el documento. Con varias facturas no se puede elegir una.
 */
export const puedeSincronizarPago = (value) => dividirComprobantes(value).length === 1

/**
 * True cuando hay que avisarle al usuario que el comprobante guardado no se
 * puede asociar: tiene pinta de número de documento (algún dígito) pero no se
 * resuelve a serie + número. Marcadores intencionales sin factura ("SN",
 * "NO FACTURAR", "ESPERAR CONFIRMACIÓN") no generan aviso.
 */
export const requiereAvisoComprobante = (value) => {
    const texto = typeof value === 'number' && Number.isFinite(value) ? String(value) : value
    if (typeof texto !== 'string') return false
    const limpio = texto.trim()
    if (!limpio) return false
    if (puedeSincronizarPago(limpio)) return false
    return /\d/.test(limpio)
}
